import { DayObject, TimeOfDay } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import { HotelBookingSummary } from "@/types/hotels/hotels"
import { ItineraryEvent } from "@/types/itinerary"
import { Room, TravelerGroup } from "@/types/trip"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "./redux-store"

export type PlaceSearchParams = {
  startDate: DayObject | undefined
  endDate: DayObject | undefined
  travelers: TravelerGroup | undefined
  room: Room | undefined
  reservationTime: TimeOfDay | undefined
}

export interface PlaceSliceState {
  communityDialog: {
    open: boolean
    placeRef: PlaceRef | undefined
    selectedMentionId: string | undefined // Single mention view
    selectedReviewId: string | undefined // Single review view
  }
  bookedExternalDialogOpen: boolean

  searchParams: PlaceSearchParams | undefined

  itineraryEvent: ItineraryEvent | undefined
  hotelBooking: HotelBookingSummary | undefined
  scheduleStartAt: number | undefined // for matching schedule start_at timestamp for event places
}

export const placeSliceInitialState: PlaceSliceState = {
  communityDialog: {
    open: false,
    placeRef: undefined,
    selectedMentionId: undefined,
    selectedReviewId: undefined,
  },
  bookedExternalDialogOpen: false,
  searchParams: undefined,
  itineraryEvent: undefined,
  hotelBooking: undefined,
  scheduleStartAt: undefined,
}

const placeSlice = createSlice({
  name: "place",
  initialState: placeSliceInitialState,
  reducers: {
    reset: () => placeSliceInitialState,
    openCommunityDialog: (
      state,
      action: PayloadAction<{
        placeRef: PlaceRef
        mentionId?: string
        reviewId?: string
      }>
    ) => {
      state.communityDialog = {
        open: true,
        placeRef: action.payload.placeRef,
        selectedMentionId: action.payload.mentionId,
        selectedReviewId: action.payload.reviewId,
      }
    },
    closeCommunityDialog: (state) => {
      state.communityDialog.open = false
    },
    setBookedExternalDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.bookedExternalDialogOpen = action.payload
    },
    setSearchParams: (
      state,
      action: PayloadAction<PlaceSearchParams | undefined>
    ) => {
      state.searchParams = action.payload
    },
    updateSearchParams: (
      state,
      action: PayloadAction<Partial<PlaceSearchParams>>
    ) => {
      if (!state.searchParams) {
        state.searchParams = {
          startDate: undefined,
          endDate: undefined,
          travelers: undefined,
          room: undefined,
          reservationTime: undefined,
        }
      }
      if (action.payload.startDate !== undefined) {
        state.searchParams.startDate = action.payload.startDate
      }
      if (action.payload.endDate !== undefined) {
        state.searchParams.endDate = action.payload.endDate
      }
      if (action.payload.travelers !== undefined) {
        state.searchParams.travelers = action.payload.travelers
      }
      if (action.payload.room !== undefined) {
        state.searchParams.room = action.payload.room
      }
      if (action.payload.reservationTime !== undefined) {
        state.searchParams.reservationTime = action.payload.reservationTime
      }
    },
    // Keep old actions for backwards compatibility
    setStartDate: (state, action: PayloadAction<DayObject | undefined>) => {
      if (!state.searchParams) {
        state.searchParams = {
          startDate: undefined,
          endDate: undefined,
          travelers: undefined,
          room: undefined,
          reservationTime: undefined,
        }
      }
      state.searchParams.startDate = action.payload
    },
    setEndDate: (state, action: PayloadAction<DayObject | undefined>) => {
      if (!state.searchParams) {
        state.searchParams = {
          startDate: undefined,
          endDate: undefined,
          travelers: undefined,
          room: undefined,
          reservationTime: undefined,
        }
      }
      state.searchParams.endDate = action.payload
    },
    setTravelers: (state, action: PayloadAction<TravelerGroup | undefined>) => {
      if (!state.searchParams) {
        state.searchParams = {
          startDate: undefined,
          endDate: undefined,
          travelers: undefined,
          room: undefined,
          reservationTime: undefined,
        }
      }
      state.searchParams.travelers = action.payload
    },
    setRoom: (state, action: PayloadAction<Room | undefined>) => {
      if (!state.searchParams) {
        state.searchParams = {
          startDate: undefined,
          endDate: undefined,
          travelers: undefined,
          room: undefined,
          reservationTime: undefined,
        }
      }
      state.searchParams.room = action.payload
    },
    setHotelBooking: (
      state,
      action: PayloadAction<HotelBookingSummary | undefined>
    ) => {
      state.hotelBooking = action.payload
    },
    setItineraryEvent: (
      state,
      action: PayloadAction<ItineraryEvent | undefined>
    ) => {
      state.itineraryEvent = action.payload
    },
    setScheduleStartAt: (state, action: PayloadAction<number | undefined>) => {
      state.scheduleStartAt = action.payload
    },
  },
})

const selectSliceState = (state: RootState) => state.placeSlice

export const selectCommunityDialog = createSelector(
  selectSliceState,
  (state) => state.communityDialog
)

export const selectCommunityDialogOpen = createSelector(
  selectCommunityDialog,
  (communityDialog) => communityDialog.open
)

export const selectPlaceSearchParams = createSelector(
  selectSliceState,
  (state) => state.searchParams
)

export const selectPlaceStartDate = createSelector(
  [selectPlaceSearchParams],
  (searchParams) => searchParams?.startDate
)

export const selectPlaceEndDate = createSelector(
  [selectPlaceSearchParams],
  (searchParams) => searchParams?.endDate
)

export const selectPlaceTravelers = createSelector(
  [selectPlaceSearchParams],
  (searchParams) => searchParams?.travelers
)

export const selectPlaceRoom = createSelector(
  [selectPlaceSearchParams],
  (searchParams) => searchParams?.room
)

export const selectPlaceReservationTime = createSelector(
  [selectPlaceSearchParams],
  (searchParams) => searchParams?.reservationTime
)

export const selectHotelBooking = createSelector(
  selectSliceState,
  (state) => state.hotelBooking
)

export const selectHasConfirmedHotelBooking = createSelector(
  selectHotelBooking,
  (hotelBooking) => hotelBooking?.status === "confirmed"
)

export const selectBookedDirectDialogOpen = createSelector(
  selectSliceState,
  (state) => state.bookedExternalDialogOpen
)

export const selectItineraryEvent = createSelector(
  selectSliceState,
  (state) => state.itineraryEvent
)

export const selectPlaceScheduleStartAt = createSelector(
  selectSliceState,
  (state) => state.scheduleStartAt
)

export default placeSlice
